import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { userAPI, walletAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const WalletToWalletTransferModal = ({ show, onHide, onSuccess, onBalanceUpdate }) => {
  const { user: currentUser } = useAuth();
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searching, setSearching] = useState(false);

  const handleSearch = async (q) => {
    setRecipientQuery(q);
    setSearching(true);
    setSelectedRecipient(null);
    setUserId('');
    if (q.length < 2) {
      setRecipientResults([]);
      setSearching(false);
      return;
    }
    try {
      const res = await userAPI.userSearch(q);
      // Filter out current user from search results
      const filteredResults = res.data.users.filter(user => user.id !== currentUser.id);
      setRecipientResults(filteredResults);
    } catch (e) {
      setRecipientResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectRecipient = (user) => {
    // Prevent selecting current user
    if (user.id === currentUser.id) {
      setError('Cannot transfer to yourself.');
      return;
    }
    setSelectedRecipient(user);
    setUserId(user.custom_id || user.id);
    setRecipientQuery(user.name + (user.email ? ` (${user.email})` : ''));
    setRecipientResults([]);
    setError(''); // Clear any previous errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with:', { userId, amount, note, selectedRecipient });
    setError('');
    setSuccess('');
    setLoading(true);
    
    // Additional validation to prevent self-transfer
    if (userId == currentUser.id) {
      setError('Cannot transfer to yourself.');
      setLoading(false);
      return;
    }
    
    try {
      const transferData = {
        receiver_id: userId,
        amount,
        note,
      };
      console.log('Sending transfer data:', transferData);
      const response = await walletAPI.walletToWalletTransfer(transferData);
      console.log('Transfer response:', response.data);
      
      setSuccess(`Transfer successful! Your new balance is ₹${response.data.sender_balance}`);
      setAmount('');
      setNote('');
      setUserId('');
      setRecipientQuery('');
      setSelectedRecipient(null);
      
      // Update balance in parent component
      if (onBalanceUpdate) {
        onBalanceUpdate(); // For admin, this will re-fetch the balance
      }
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.response?.data?.message || 'Transfer failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setAmount('');
    setNote('');
    setUserId('');
    setRecipientQuery('');
    setSelectedRecipient(null);
    setRecipientResults([]);
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="modal-responsive">
      <Modal.Header closeButton>
        <Modal.Title className="text-lg sm:text-xl">Wallet to Wallet Transfer</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3 position-relative">
            <Form.Label>Recipient (search by name/email/phone or enter User ID)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Type name, email, phone, or user ID"
              value={recipientQuery}
              onChange={e => handleSearch(e.target.value)}
              autoComplete="off"
              disabled={loading}
              style={{ fontWeight: selectedRecipient ? 'bold' : 'normal' }}
            />
            {recipientResults.length > 0 && (
              <ListGroup className="mt-1 position-absolute w-100" style={{ zIndex: 10, minWidth: '100%', maxWidth: '100%' }}>
                {recipientResults.map(user => (
                  <ListGroup.Item key={user.id} action onClick={() => handleSelectRecipient(user)} style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontSize: '1.1em' }}><strong>{user.name}</strong> <span className="text-muted">({user.role})</span></div>
                      <div style={{ fontSize: '0.95em', color: '#333' }} className="d-flex flex-column flex-sm-row flex-wrap">
                        <span className="me-2">Email: {user.email || '-'}</span>
                        <span className="me-2 d-none d-sm-inline">|</span>
                        <span className="me-2">Phone: {user.phone || '-'}</span>
                        <span className="me-2 d-none d-sm-inline">|</span>
                        <span>User ID: {user.custom_id || user.id}</span>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {recipientResults.length === 0 && searching && (
              <div className="mt-1 text-muted text-sm">Searching...</div>
            )}
            {recipientResults.length === 0 && !searching && recipientQuery.length >= 2 && (
              <div className="mt-1 text-muted text-sm">No users found matching your search.</div>
            )}
            <Form.Text className="text-muted">
              Or enter User ID directly below.
            </Form.Text>
            <Form.Control
              className="mt-2"
              type="text"
              placeholder="User ID (min 10 chars)"
              value={userId}
              minLength={10}
              onChange={e => { 
                const newUserId = e.target.value;
                setUserId(newUserId);
                setSelectedRecipient(null);
                setRecipientQuery('');
                if (newUserId == (currentUser.custom_id || currentUser.id)) {
                  setError('Cannot transfer to yourself.');
                } else {
                  setError('');
                }
              }}
              disabled={loading || !!selectedRecipient}
              style={{ marginBottom: 8 }}
            />
            {/* Auto-filled fields for Email and Phone */}
            <div className="d-flex gap-2 mb-2">
              <Form.Control
                type="text"
                placeholder="Email"
                value={selectedRecipient ? selectedRecipient.email || '' : ''}
                readOnly
                style={{ background: '#f8f9fa', width: '50%' }}
              />
              <Form.Control
                type="text"
                placeholder="Phone"
                value={selectedRecipient ? selectedRecipient.phone || '' : ''}
                readOnly
                style={{ background: '#f8f9fa', width: '50%' }}
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              min={1}
              required
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add a note for the recipient"
              value={note}
              onChange={e => setNote(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {/* Cancel Button (Bootstrap) */}
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {/* Send Money Button (Custom) */}
          <button
            type="submit"
            disabled={loading || !userId || !amount}
            onClick={() => console.log('Button clicked. State:', { loading, userId, amount })}
            style={{
              backgroundColor: '#ffff66 !important', // bright yellow for debug
              color: '#800000 !important',
              border: '2px solid #800000',
              fontWeight: 'bold',
              minWidth: '120px',
              minHeight: '40px',
              fontSize: '1.1em',
              lineHeight: '1.1em',
              boxShadow: 'none',
              borderRadius: '8px',
              cursor: loading || !userId || !amount ? 'not-allowed' : 'pointer',
              transition: 'none',
              outline: 'none',
              display: 'inline-block',
              textAlign: 'center',
              verticalAlign: 'middle',
              userSelect: 'none',
              marginLeft: '12px',
            }}
          >
            TEST {loading ? <Spinner size="sm" /> : 'Send Money'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WalletToWalletTransferModal; 